import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import PublicAccessManager from './PublicAccessManager';
import { api } from '../utils/apiClient';
import { useTheme } from '../contexts/ThemeContext';
import { clearSettingsCache } from '../services/settingsService';
import './Settings.css';

const Settings = () => {
  const { updateTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeSection, setActiveSection] = useState('general');
  
  // General Settings
  const [generalSettings, setGeneralSettings] = useState({
    dashboardRefreshInterval: 30,
  });

  // Monitoring & Scan Configuration
  const [monitoringSettings, setMonitoringSettings] = useState({
    uptimeCheckFrequency: 5,
    healthScanInterval: 60,
    seoScanEnabled: true,
    severityThresholdHealthy: 95,
    severityThresholdWarning: 80,
    severityThresholdCritical: 60,
  });

  // Notifications & Alerts
  const [notificationSettings, setNotificationSettings] = useState({
    notificationEmailEnabled: true,
    alertOnDowntime: true,
    alertOnSecurityIssues: true,
    alertOnPluginUpdates: true,
    alertOnBackupFailures: true,
  });

  // Backup & Storage (restore removed - no restore permissions)
  const [backupSettings, setBackupSettings] = useState({});

  // WordPress Integration
  const [wpSettings, setWpSettings] = useState({
    syncPluginsEnabled: true,
    syncThemesEnabled: true,
    syncVersionsEnabled: true,
    healthScanEnabled: true,
    securityScanEnabled: true,
  });

  // Logs & Data Retention
  const [logsSettings, setLogsSettings] = useState({
    activityLogRetentionDays: 90,
    pluginUpdateLogRetentionDays: 180,
    reportsRetentionDays: 365,
  });


  // System Preferences
  // Note: UI Theme, Table Pagination Size, and Default Filter controls have been removed from the UI.
  const [systemSettings, setSystemSettings] = useState({
    apiRateLimit: 100,
  });

  // Load all settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/settings');
      const settings = response.data.settings || {};

      // Populate state with loaded settings
      setGeneralSettings({
        dashboardRefreshInterval: settings.dashboardRefreshInterval || 30,
      });

      setMonitoringSettings({
        uptimeCheckFrequency: settings.uptimeCheckFrequency || 5,
        healthScanInterval: settings.healthScanInterval || 60,
        seoScanEnabled: settings.seoScanEnabled !== undefined ? settings.seoScanEnabled : true,
        severityThresholdHealthy: settings.severityThresholdHealthy || 95,
        severityThresholdWarning: settings.severityThresholdWarning || 80,
        severityThresholdCritical: settings.severityThresholdCritical || 60,
      });

      setNotificationSettings({
        notificationEmailEnabled: settings.notificationEmailEnabled !== undefined ? settings.notificationEmailEnabled : true,
        alertOnDowntime: settings.alertOnDowntime !== undefined ? settings.alertOnDowntime : true,
        alertOnSecurityIssues: settings.alertOnSecurityIssues !== undefined ? settings.alertOnSecurityIssues : true,
        alertOnPluginUpdates: settings.alertOnPluginUpdates !== undefined ? settings.alertOnPluginUpdates : true,
        alertOnBackupFailures: settings.alertOnBackupFailures !== undefined ? settings.alertOnBackupFailures : true,
      });

      setBackupSettings({});

      setWpSettings({
        syncPluginsEnabled: settings.syncPluginsEnabled !== undefined ? settings.syncPluginsEnabled : true,
        syncThemesEnabled: settings.syncThemesEnabled !== undefined ? settings.syncThemesEnabled : true,
        syncVersionsEnabled: settings.syncVersionsEnabled !== undefined ? settings.syncVersionsEnabled : true,
        healthScanEnabled: settings.healthScanEnabled !== undefined ? settings.healthScanEnabled : true,
        securityScanEnabled: settings.securityScanEnabled !== undefined ? settings.securityScanEnabled : true,
      });

      setLogsSettings({
        activityLogRetentionDays: settings.activityLogRetentionDays || 90,
        pluginUpdateLogRetentionDays: settings.pluginUpdateLogRetentionDays || 180,
        reportsRetentionDays: settings.reportsRetentionDays || 365,
      });

      setSystemSettings({
        apiRateLimit: settings.apiRateLimit || 100,
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings.' });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (section) => {
    try {
      setSaving(true);
      setMessage(null);

      let settingsToSave = {};
      
      switch (section) {
        case 'general':
          settingsToSave = generalSettings;
          break;
        case 'monitoring':
          settingsToSave = monitoringSettings;
          break;
        case 'notifications':
          settingsToSave = notificationSettings;
          break;
        case 'backup':
          settingsToSave = backupSettings;
          break;
        case 'wordpress':
          settingsToSave = wpSettings;
          break;
        case 'logs':
          settingsToSave = logsSettings;
          break;
        case 'system':
          settingsToSave = systemSettings;
          break;
        default:
          // Save all settings
          settingsToSave = {
            ...generalSettings,
            ...monitoringSettings,
            ...notificationSettings,
            ...backupSettings,
            ...wpSettings,
            ...logsSettings,
            ...systemSettings,
          };
      }

      await api.put('/api/settings', { settings: settingsToSave });
      
      // Clear settings cache so fresh values are fetched
      clearSettingsCache();
      
      // Reload settings from server to ensure UI reflects saved values
      await loadSettings();
      
      // Dispatch event to notify other components (like dashboard) that settings have changed
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('settings-updated', { detail: { section, settings: settingsToSave } }));
      }
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      await api.post('/api/settings/reset');
      await loadSettings();
      setMessage({ type: 'success', text: 'Settings reset to defaults successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to reset settings:', error);
      setMessage({ type: 'error', text: 'Failed to reset settings.' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    const setters = {
      general: setGeneralSettings,
      monitoring: setMonitoringSettings,
      notifications: setNotificationSettings,
      backup: setBackupSettings,
      wordpress: setWpSettings,
      logs: setLogsSettings,
      system: setSystemSettings,
    };

    const setter = setters[section];
    if (setter) {
      setter((prev) => ({ ...prev, [field]: value }));
    }
  };

  const toggleField = (section, field) => {
    const setters = {
      general: setGeneralSettings,
      monitoring: setMonitoringSettings,
      notifications: setNotificationSettings,
      backup: setBackupSettings,
      wordpress: setWpSettings,
      logs: setLogsSettings,
      system: setSystemSettings,
    };

    const getters = {
      general: generalSettings,
      monitoring: monitoringSettings,
      notifications: notificationSettings,
      backup: backupSettings,
      wordpress: wpSettings,
      logs: logsSettings,
      system: systemSettings,
    };

    const setter = setters[section];
    const getter = getters[section];
    if (setter && getter) {
      setter((prev) => ({ ...prev, [field]: !prev[field] }));
    }
  };

  const toggleRole = (section, field, role) => {
    const setters = {
      backup: setBackupSettings,
    };

    const getters = {
      backup: backupSettings,
    };

    const setter = setters[section];
    const getter = getters[section];
    if (setter && getter) {
      const currentRoles = getter[field] || [];
      const newRoles = currentRoles.includes(role)
        ? currentRoles.filter((r) => r !== role)
        : [...currentRoles, role];
      setter((prev) => ({ ...prev, [field]: newRoles }));
    }
  };


  if (loading) {
    return (
      <div className="dashboard-container settings-page">
        <Sidebar />
        <div className="settings-main">
          <div className="loading-spinner">Loading settings...</div>
        </div>
      </div>
    );
  }

  const sections = [
    { id: 'general', name: 'General Settings', icon: '⚙️' },
    { id: 'monitoring', name: 'Monitoring & Scan', icon: '📊' },
    { id: 'notifications', name: 'Notifications & Alerts', icon: '🔔' },
    { id: 'backup', name: 'Backup & Storage', icon: '💾' },
    { id: 'wordpress', name: 'WordPress Integration', icon: '🔌' },
    { id: 'logs', name: 'Logs & Retention', icon: '📝' },
    { id: 'system', name: 'System Preferences', icon: '🎨' },
  ];

  return (
    <div className="dashboard-container settings-page">
      <Sidebar />
      <div className="settings-main">
        <header className="settings-hero">
          <div>
            <p className="eyebrow">Control Center</p>
            <h1>Advanced Settings</h1>
            <p className="subheading">
              Configure global system behavior, per-website preferences, and user management policies.
            </p>
          </div>
          <div className="settings-meta">
            <div>
              <span>Active Sections</span>
              <strong>{sections.length}</strong>
            </div>
            <div>
              <span>Settings Saved</span>
              <strong>All</strong>
            </div>
          </div>
        </header>

        {message && (
          <div className={`settings-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="settings-layout">
          <nav className="settings-nav">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="nav-icon">{section.icon}</span>
                <span className="nav-label">{section.name}</span>
              </button>
            ))}
          </nav>

          <div className="settings-content">
            {/* General Settings */}
            {activeSection === 'general' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>General Settings</h2>
                  <p>Configure application-wide preferences and defaults.</p>
                </div>
                <div className="form-grid">
                  <label>
                    Dashboard Refresh Interval (seconds)
                    <input
                      type="number"
                      min="5"
                      max="300"
                      value={generalSettings.dashboardRefreshInterval || 30}
                      onChange={(e) => {
                        const numValue = parseInt(e.target.value, 10);
                        if (!isNaN(numValue)) {
                          updateField('general', 'dashboardRefreshInterval', numValue);
                        }
                      }}
                    />
                  </label>
                </div>
                <div className="card-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => saveSettings('general')}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save General Settings'}
                  </button>
                </div>
              </div>
            )}

            {/* Monitoring & Scan Configuration */}
            {activeSection === 'monitoring' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>Monitoring & Scan Configuration</h2>
                  <p>Define uptime check frequency, health scan intervals, and severity thresholds.</p>
                </div>
                <div className="form-grid">
                  <label>
                    Uptime Check Frequency (minutes)
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={monitoringSettings.uptimeCheckFrequency}
                      onChange={(e) => updateField('monitoring', 'uptimeCheckFrequency', parseInt(e.target.value))}
                    />
                  </label>
                  <label>
                    Health Scan Interval (minutes)
                    <input
                      type="number"
                      min="5"
                      max="1440"
                      value={monitoringSettings.healthScanInterval}
                      onChange={(e) => updateField('monitoring', 'healthScanInterval', parseInt(e.target.value))}
                    />
                  </label>
                  <label>
                    Healthy Threshold (%)
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={monitoringSettings.severityThresholdHealthy}
                      onChange={(e) => updateField('monitoring', 'severityThresholdHealthy', parseInt(e.target.value))}
                    />
                  </label>
                  <label>
                    Warning Threshold (%)
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={monitoringSettings.severityThresholdWarning}
                      onChange={(e) => updateField('monitoring', 'severityThresholdWarning', parseInt(e.target.value))}
                    />
                  </label>
                  <label>
                    Critical Threshold (%)
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={monitoringSettings.severityThresholdCritical}
                      onChange={(e) => updateField('monitoring', 'severityThresholdCritical', parseInt(e.target.value))}
                    />
                  </label>
                </div>
                <div className="toggle-row">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={monitoringSettings.seoScanEnabled}
                      onChange={() => toggleField('monitoring', 'seoScanEnabled')}
                    />
                    <span>Enable SEO Scans</span>
                  </label>
                </div>
                <div className="card-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => saveSettings('monitoring')}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Monitoring Settings'}
                  </button>
                </div>
              </div>
            )}

            {/* Notifications & Alerts */}
            {activeSection === 'notifications' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>Notifications & Alerts</h2>
                  <p>Configure alert delivery channels and notification rules.</p>
                </div>
                <div className="toggle-row">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.notificationEmailEnabled}
                      onChange={() => toggleField('notifications', 'notificationEmailEnabled')}
                    />
                    <span>Enable Email Notifications</span>
                  </label>
                </div>
                <div className="section-divider">
                  <h3>Alert Rules</h3>
                </div>
                <div className="toggle-row">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.alertOnDowntime}
                      onChange={() => toggleField('notifications', 'alertOnDowntime')}
                    />
                    <span>Alert on Downtime</span>
                  </label>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.alertOnSecurityIssues}
                      onChange={() => toggleField('notifications', 'alertOnSecurityIssues')}
                    />
                    <span>Alert on Security Issues</span>
                  </label>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.alertOnPluginUpdates}
                      onChange={() => toggleField('notifications', 'alertOnPluginUpdates')}
                    />
                    <span>Alert on Plugin Updates</span>
                  </label>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.alertOnBackupFailures}
                      onChange={() => toggleField('notifications', 'alertOnBackupFailures')}
                    />
                    <span>Alert on Backup Failures</span>
                  </label>
                </div>
                <div className="card-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => saveSettings('notifications')}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Notification Settings'}
                  </button>
                </div>
              </div>
            )}

            {/* Backup & Storage */}
            {activeSection === 'backup' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>Backup & Storage</h2>
                  <p>Backup configuration is managed from the Backup &amp; Restore page.</p>
                </div>
              </div>
            )}

            {/* WordPress Integration */}
            {activeSection === 'wordpress' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>WordPress Integration</h2>
                  <p>Control sync behavior for plugins, themes, versions, and log collection.</p>
                </div>
                <div className="toggle-row">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={wpSettings.syncPluginsEnabled}
                      onChange={() => toggleField('wordpress', 'syncPluginsEnabled')}
                    />
                    <span>Sync Plugins</span>
                  </label>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={wpSettings.syncThemesEnabled}
                      onChange={() => toggleField('wordpress', 'syncThemesEnabled')}
                    />
                    <span>Sync Themes</span>
                  </label>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={wpSettings.syncVersionsEnabled}
                      onChange={() => toggleField('wordpress', 'syncVersionsEnabled')}
                    />
                    <span>Sync Versions</span>
                  </label>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={wpSettings.healthScanEnabled}
                      onChange={() => toggleField('wordpress', 'healthScanEnabled')}
                    />
                    <span>Enable Health Scans</span>
                  </label>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={wpSettings.securityScanEnabled}
                      onChange={() => toggleField('wordpress', 'securityScanEnabled')}
                    />
                    <span>Enable Security Scans</span>
                  </label>
                </div>
                <div className="card-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => saveSettings('wordpress')}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save WordPress Settings'}
                  </button>
                </div>
              </div>
            )}

            {/* Logs & Data Retention */}
            {activeSection === 'logs' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>Logs & Data Retention</h2>
                  <p>Configure how long activity logs, plugin update logs, and reports are stored.</p>
                </div>
                <div className="form-grid">
                  <label>
                    Activity Log Retention (days)
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={logsSettings.activityLogRetentionDays}
                      onChange={(e) => updateField('logs', 'activityLogRetentionDays', parseInt(e.target.value))}
                    />
                  </label>
                  <label>
                    Plugin Update Log Retention (days)
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={logsSettings.pluginUpdateLogRetentionDays}
                      onChange={(e) => updateField('logs', 'pluginUpdateLogRetentionDays', parseInt(e.target.value))}
                    />
                  </label>
                  <label>
                    Reports Retention (days)
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={logsSettings.reportsRetentionDays}
                      onChange={(e) => updateField('logs', 'reportsRetentionDays', parseInt(e.target.value))}
                    />
                  </label>
                </div>
                <div className="card-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => saveSettings('logs')}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Logs Settings'}
                  </button>
                </div>
              </div>
            )}

            {/* System Preferences (UI Theme, Table Pagination Size, and Default Filter removed) */}
            {activeSection === 'system' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>System Preferences</h2>
                  <p>Configure global system-level options such as API rate limits.</p>
                </div>
                <div className="form-grid">
                  <label>
                    API Rate Limit (requests/minute)
                    <input
                      type="number"
                      min="10"
                      max="1000"
                      value={systemSettings.apiRateLimit}
                      onChange={(e) => updateField('system', 'apiRateLimit', parseInt(e.target.value))}
                    />
                  </label>
                </div>
                <div className="card-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => saveSettings('system')}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save System Settings'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="settings-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={resetToDefaults}
            disabled={saving}
          >
            Reset All to Defaults
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
